import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { SessionService } from '@evertel/session/SessionService'
import { PollConfig } from '@evertel/session/PollConfig'
import { Api } from '@evertel/api'
import { APIDataBlueUser, APIDataRoom, APIDataThread } from '@evertel/types'
import { DepartmentsAccessStore } from '@evertel/departments-access'
import { getRole } from '@evertel/utils'
import { BlueUserStore, FetchUserService, UnreadCountsState } from '@evertel/blue-user'
import { RoomStore, ThreadStore, DisplayThread } from '@evertel/stores'
import { LocalAsyncStorage } from '@evertel/utils'


const GET_ROOMS_AND_THREADS_POLL_INTERVAL_MS = 1000 * 15 // 15 seconds

interface SectionVisibility {
    documents: boolean
    interagency: boolean
    agencyWide: boolean
    team: boolean
    threads: boolean
}

const DEFAULT_SECTION_VISIBILITY: SectionVisibility = {
    documents: true,
    interagency: true,
    agencyWide: true,
    team: true,
    threads: true
}

class DrawerController {
    private pollInterval = undefined // holds the setInterval timer

    // Section visibility state
    public sectionVisibility: SectionVisibility = { ...DEFAULT_SECTION_VISIBILITY }

    // Whether the closed (hidden) threads list is shown in the drawer.
    // Transient by design: defaults to hidden on every app start
    public showClosedThreads = false
    public isLoadingClosedThreads = false

    constructor(
        private api: Api,
        private session: SessionService,
        private userStore: BlueUserStore,
        private departmentsAccessStore: DepartmentsAccessStore,
        private unreadState: UnreadCountsState,
        private roomStore: RoomStore,
        private threadStore: ThreadStore,
        private localStorage: LocalAsyncStorage,
        private fetchUserService: FetchUserService
    ) {
        makeAutoObservable(this)
        this.loadSectionVisibility()
    }

    // Section visibility management
    toggleSectionVisibility = async (sectionKey: keyof SectionVisibility) => {
        runInAction(() => {
            this.sectionVisibility[sectionKey] = !this.sectionVisibility[sectionKey]
        })
        await this.saveSectionVisibility()
    }

    setSectionVisibility = async (sectionKey: keyof SectionVisibility, visible: boolean) => {
        runInAction(() => {
            this.sectionVisibility[sectionKey] = visible
        })
        await this.saveSectionVisibility()
    }

    private loadSectionVisibility = async () => {
        try {
            const saved = await this.localStorage.getItem('drawerSectionVisibility')
            if (saved) {
                const state = JSON.parse(saved)
                runInAction(() => {
                    this.sectionVisibility = { ...DEFAULT_SECTION_VISIBILITY, ...state }
                })
            }
        } catch (error) {
            console.error('Failed to load drawer section visibility:', error)
        }
    }

    private saveSectionVisibility = async () => {
        try {
            await this.localStorage.setItem('drawerSectionVisibility', JSON.stringify(this.sectionVisibility))
        } catch (error) {
            console.error('Failed to save drawer section visibility:', error)
        }
    }

    startPoll = () => {
        this.stopPoll()
        if (!PollConfig.globalEnabled) return

        this.pollInterval = setInterval(() => {
            Promise.all([
                this.fetchActiveRooms(),
                this.fetchActiveThreads()
            ]).catch((err) => {
                /** noop */
            })
        }, GET_ROOMS_AND_THREADS_POLL_INTERVAL_MS)
    }

    stopPoll = () => {
        if (this.pollInterval) {
            clearInterval(this.pollInterval)
            this.pollInterval = undefined
        }
    }

    // Extend the interval timer if its set
    resetPollInterval = () => {
        if (this.pollInterval) {
            this.startPoll()
        }
    }

    fetchActiveRooms = async (): Promise<void> => {
        this.resetPollInterval()
        const roomsCount = (await this.api.Routes.BlueUser.getRoomsCount(this.session.currentUserId, {
            where: {
                isArchived: false
            }
        }))?.count || 0

        const pages = Math.ceil(roomsCount / 100)
        const promises: Promise<APIDataRoom[]>[] = []

        for (let i = 0; i < pages; i++) {
            promises.push(this.api.Routes.BlueUser.getRooms(this.session.currentUserId, {
                where: { isArchived: false },
                skip: 100 * i,
                limit: 100
            }))
        }

        try {
            const fetchedRooms = await Promise.all(promises)

            const rooms = fetchedRooms.filter(Boolean).flat()

            const roomIds = rooms.map((r: APIDataRoom) => r.id)

            runInAction(() => {
                this.roomStore.update(rooms)
                this.session.setRoomIdsIBelongTo(roomIds)
            })

        } catch (error: any) {
            console.error(error.message)
        }
    }

    fetchActiveThreads = async () => {
        this.resetPollInterval()
        const threadsCount = (await this.api.Routes.BlueUser.getThreadsCount(this.session.currentUserId))?.count || 0

        const pages = Math.ceil(threadsCount / 100)
        const promises: Promise<APIDataThread[]>[] = []

        for (let i = 0; i < pages; i++) {
            promises.push(this.api.Routes.BlueUser.getThreads(this.session.currentUserId, {
                include: {
                    relation: 'users',
                    scope: {
                        fields: ['id', 'firstName', 'lastName']
                    }
                },
                skip: 100 * i,
                limit: 100
            }))
        }

        try {
            const fetchedThreads = await Promise.all(promises)
            const flatThreads = fetchedThreads.filter(Boolean).flat()

            this.decorateAndStoreThreads(flatThreads, false)

        } catch (error: any) {
            console.error(error.message)
        }
    }

    // Fetches threads the current user has closed (hidden). The API maps
    // "closed" via a 'disabled' flag in the where clause: it flips the
    // ThreadUserThrough query to isEnabled=false (see BlueUser/get-threads.js)
    fetchClosedThreads = async () => {
        runInAction(() => {
            this.isLoadingClosedThreads = true
        })

        try {
            const threadsCount = (await this.api.Routes.BlueUser.getThreadsCount(this.session.currentUserId, {
                disabled: true
            }))?.count || 0

            const pages = Math.ceil(threadsCount / 100)
            const promises: Promise<APIDataThread[]>[] = []

            for (let i = 0; i < pages; i++) {
                promises.push(this.api.Routes.BlueUser.getThreads(this.session.currentUserId, {
                    where: { disabled: true },
                    include: {
                        relation: 'users',
                        scope: {
                            fields: ['id', 'firstName', 'lastName']
                        }
                    },
                    skip: 100 * i,
                    limit: 100
                }))
            }

            const fetchedThreads = await Promise.all(promises)
            const flatThreads = fetchedThreads.filter(Boolean).flat()

            this.decorateAndStoreThreads(flatThreads, true)

        } catch (error: any) {
            console.error(error.message)
        } finally {
            runInAction(() => {
                this.isLoadingClosedThreads = false
            })
        }
    }

    toggleClosedThreads = async () => {
        const show = !this.showClosedThreads

        runInAction(() => {
            this.showClosedThreads = show
        })

        // collapse the Direct Messages section when showing closed DMs so the
        // new header is visible right below it. The eye never expands DMs
        if (show && this.sectionVisibility.threads) {
            await this.setSectionVisibility('threads', false)
        }

        // refetch on every expand so the list is always fresh
        if (show) {
            await this.fetchClosedThreads()
        }
    }

    // Decorates raw API threads for display and merges them into the store,
    // marking them open (isClosed=false) or closed (isClosed=true)
    private decorateAndStoreThreads = (flatThreads: APIDataThread[], isClosed: boolean) => {
        // collect unique participant ids across all threads, excluding current user
        const allUserIds = [...new Set(flatThreads.flatMap(t => t.users?.map(u => u.id) ?? []))]
            .filter(id => id !== this.session.currentUserId)

        // add display fields to thread object
        const threads = flatThreads as DisplayThread[]

        threads.forEach(t => {
            t.userIds = t.users?.map(u => u.id)
            t.name = this.getThreadName(t.users)
            t.isBotThread = !!t.meta?.isBotThread
            t.isClosed = isClosed
        })

        runInAction(() => {
            this.threadStore.update(threads)
        })

        // fetch full user profiles via FetchUserService (fire-and-forget)
        if (allUserIds.length) {
            this.fetchUserService.fetchUsersFromThreads(allUserIds)
        }
    }

    getThreadName = (users: APIDataBlueUser[]) => {
        if (!users?.length) return null

        const otherUsers = users?.filter(u => u.id !== this.session.currentUserId)
        const isJustMe = users?.length === 1 && users[0].id === this.session.currentUserId

        if (isJustMe) {
            return `${users[0].firstName} ${users[0].lastName}`
        } else if (otherUsers?.length === 1) {
            return `${otherUsers[0].firstName} ${otherUsers[0].lastName}`
        } else {
            const names = otherUsers?.map(u => u.firstName?.charAt(0) + '. ' + u.lastName)
            return names?.join(', ')
        }
    }

    closeThread = async (threadId: number) => {
        await this.api.Routes.Thread.delById(threadId)

        runInAction(() => {
            // keep the thread in the store, flagged closed, so it can show
            // up in the closed conversations list
            this.threadStore.update({ id: threadId, isClosed: true } as DisplayThread)
        })
    }

    reopenThread = async (threadId: number) => {
        await this.api.Routes.Thread.postReopen(threadId)

        runInAction(() => {
            this.threadStore.update({ id: threadId, isClosed: false } as DisplayThread)
        })
    }

    sortRooms = (roomArray: APIDataRoom[]) => {
        const user = this.userStore.findById(this.session.currentUserId)
        const userMeta = user?.meta as any
        const sortType = userMeta?.appearance?.drawerRoomSort

        switch (sortType) {
            case 'alpha':
                return this.sortRoomsAlpha(roomArray)
            case 'newest':
                return roomArray.sort((a: APIDataRoom, b: APIDataRoom) => Date.parse(String(b.lastActivityDate)) - Date.parse(String(a.lastActivityDate)))
            case 'oldest':
                return roomArray.sort((a: APIDataRoom, b: APIDataRoom) => Date.parse(String(a.lastActivityDate)) - Date.parse(String(b.lastActivityDate)))
            case 'unread':
            default: {
                const unread = roomArray.filter(r => this.unreadState.getRoomUnread(r.id as number)?.count > 0)
                const read = roomArray.filter(r => !this.unreadState.getRoomUnread(r.id as number) ||
                    this.unreadState.getRoomUnread(r.id as number)?.count <= 0)

                // unread first stored as alpha, then read sorted as alpha
                return this.sortRoomsAlpha(unread).concat(this.sortRoomsAlpha(read))
            }
        }
    }

    sortRoomsAlpha = (arr: APIDataRoom[]) => {
        return arr.sort((a: any, b: any) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()))
    }

    get sortedThreads() {
        const threadArray = this.threadStore.objectsArray.filter(t => !t.isClosed)

        let sortedThreads: DisplayThread[]

        const user = this.userStore.findById(this.session.currentUserId)
        const userMeta = user?.meta as any
        const sortType = userMeta?.appearance?.drawerThreadSort || 'newest'

        const botThread = threadArray.find(t => t?.meta?.isBotThread === true)

        if (botThread) {
            const index = threadArray.findIndex(item => item?.id === botThread?.id)
            if (index > -1) {
                threadArray.splice(index, 1)
            }
        }

        switch (sortType) {
            case 'alpha':
                sortedThreads = this.sortThreadsAlpha(threadArray)
                break
            case 'newest':
                sortedThreads = threadArray.sort((a: DisplayThread, b: DisplayThread) => Date.parse(String(b.lastActivityDate)) - Date.parse(String(a.lastActivityDate)))
                break
            case 'oldest':
                sortedThreads = threadArray.sort((a: DisplayThread, b: DisplayThread) => Date.parse(String(a.lastActivityDate)) - Date.parse(String(b.lastActivityDate)))
                break
            case 'unread':
            default: {
                const unread = threadArray.filter(r => this.unreadState.getThreadUnread(r.id as number)?.count > 0)
                const read = threadArray.filter(r => !this.unreadState.getThreadUnread(r.id as number) ||
                    this.unreadState.getThreadUnread(r.id as number)?.count <= 0)

                // unread first stored as alpha, then read sorted as alpha
                sortedThreads = this.sortThreadsAlpha(unread).concat(this.sortThreadsAlpha(read))
                break
            }
        }

        if (botThread) {
            sortedThreads.unshift(botThread)
        }

        return sortedThreads
    }

    // Threads the current user has closed (hidden), newest activity first
    get sortedClosedThreads() {
        return this.threadStore.objectsArray
            .filter(t => t.isClosed)
            .sort((a: DisplayThread, b: DisplayThread) => Date.parse(String(b.lastActivityDate)) - Date.parse(String(a.lastActivityDate)))
    }

    sortThreadsAlpha = (arr: DisplayThread[]) => {
        return arr.sort((a: DisplayThread, b: DisplayThread) => {
            return a.name?.toLocaleLowerCase().localeCompare(b.name?.toLocaleLowerCase())
        })
    }

    threadUsers = (threadId: number) => {
        const userIds = this.threadStore.findById(threadId).userIds
        if (!userIds) return null

        return this.userStore.findByIds(userIds)
    }

    get interAgencyRooms() {
        const guestDAs = this.departmentsAccessStore.find(da => (da.blueUserId === this.session.currentUserId && getRole(da.roles) === 'guest'))
        const guestDepartmentIds = guestDAs?.map(d => d.departmentId) || []
        const rooms = this.roomStore?.find((r: APIDataRoom) => (
            r.departmentId !== this.session.selectedDepartmentId
            && this.session.roomIdsIBelongTo?.includes(r.id)
            && guestDepartmentIds?.includes(r.departmentId)))
        return this.sortRooms(rooms)
    }

    get agencyWideRooms() {
        const rooms = this.roomStore?.find((r: APIDataRoom) => (r.departmentId === this.session.selectedDepartmentId && this.session.roomIdsIBelongTo?.includes(r.id) && (r.options as any)?.autoAddDepartmentMembers))
        return this.sortRooms(rooms)
    }

    get teamRooms() {
        const rooms = this.roomStore?.find((r: APIDataRoom) => (r.departmentId === this.session.selectedDepartmentId && this.session.roomIdsIBelongTo?.includes(r.id) && !(r.options as any)?.autoAddDepartmentMembers))
        return this.sortRooms(rooms)
    }

    get teamRoomsUnreadCountTotal() {
        const roomIds = this.teamRooms.map(r => r.id)
        const unreads = this.unreadState.roomUnreads.filter((u: any) => roomIds.includes(u.roomId))
        return {
            count: unreads.reduce((acc: number, obj: any) => acc + obj.count, 0),
            urgent: unreads.find((u: any) => u.urgent)
        }
    }

    get agencyWideRoomsUnreadCountTotal() {
        const roomIds = this.agencyWideRooms.map(r => r.id)
        const unreads = this.unreadState.roomUnreads.filter((u: any) => roomIds.includes(u.roomId))
        return {
            count: unreads.reduce((acc: number, obj: any) => acc + obj.count, 0),
            urgent: unreads.find((u: any) => u.urgent)
        }
    }

    get interAgencyRoomsUnreadCountTotal() {
        const roomIds = this.interAgencyRooms.map(r => r.id)
        const unreads = this.unreadState.roomUnreads.filter((u: any) => roomIds.includes(u.roomId))
        return {
            count: unreads.reduce((acc: number, obj: any) => acc + obj.count, 0),
            urgent: unreads.find((u: any) => u.urgent)
        }
    }

    get threadsUnreadCountTotal() {
        const threadIds = this.threadStore.objectsArray?.filter(t => !t.isClosed).map(t => t.id)
        const unreads = this.unreadState.threadUnreads.filter((u: any) => threadIds.includes(u.threadId))
        return {
            count: unreads.reduce((acc: number, obj: any) => acc + obj.count, 0),
            urgent: unreads.find((u: any) => u.urgent)
        }
    }
}

let i = 0
decorate(injectable(), DrawerController)
decorate(inject(Api), DrawerController, i++)
decorate(inject(SessionService), DrawerController, i++)
decorate(inject(BlueUserStore), DrawerController, i++)
decorate(inject(DepartmentsAccessStore), DrawerController, i++)
decorate(inject(UnreadCountsState), DrawerController, i++)
decorate(inject(RoomStore), DrawerController, i++)
decorate(inject(ThreadStore), DrawerController, i++)
decorate(inject(LocalAsyncStorage), DrawerController, i++)
decorate(inject(FetchUserService), DrawerController, i++)

export { DrawerController }
